/**
 * Small per-user choices that should survive a reload.
 *
 * Which date range the orders list opens on is a habit, not data: the
 * counter staff want today's work, the owner wants the month. Neither
 * should have to re-pick it on every visit, and neither should have to live
 * with the other's choice, so preferences are filed under the signed-in
 * user's id.
 *
 * localStorage, not the server: nothing here is worth a column, a migration
 * and a round trip, and losing it costs one click. Every access is wrapped
 * — a private window or blocked site data makes localStorage throw, and a
 * page that cannot remember a dropdown must still render.
 */
const KEY = 'orderbook_prefs'

const all = () => {
  try {
    const raw = localStorage.getItem(KEY)
    const parsed = raw ? JSON.parse(raw) : null
    // Anything other than a plain object — an older shape, or something
    // hand-edited — is discarded rather than reasoned about.
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

/** A remembered choice, or `fallback` when there is nothing usable. */
export function getPreference(userId, name, fallback = undefined) {
  if (userId == null) return fallback
  const mine = all()[String(userId)]
  const value = mine && typeof mine === 'object' ? mine[name] : undefined
  return value === undefined ? fallback : value
}

/** Remember a choice. Passing `undefined` forgets it. */
export function setPreference(userId, name, value) {
  if (userId == null) return
  try {
    const store = all()
    const id = String(userId)
    const mine = { ...(store[id] ?? {}) }
    if (value === undefined) delete mine[name]
    else mine[name] = value
    localStorage.setItem(KEY, JSON.stringify({ ...store, [id]: mine }))
  } catch {
    // Storage full, blocked, or a private window. The choice applies for
    // this visit and is forgotten on the next one, which is the failure
    // this feature is allowed to have.
  }
}
